/**
 * Поиск страницы — ОДИН фоновый поток на страницу (search-worker.js) для всех,
 * кто ищет: помощник на главной, поиск в сайдбаре, поиск в шапке.
 *
 * Зачем. Подготовка индекса каталога (`TimbricaSearch.prepare`: ~940
 * инструментов, ключевые слова на всех языках) — это стеммер по каждому слову.
 * Замер 23.10.2026, Edge 155, процессор ×4 (слабый ноутбук): 1,9 с одним
 * куском, а с первым поиском — 2,24 с. Сайдбар и шапка делали это НА ГЛАВНОМ
 * потоке в момент первой буквы, и каждое следующее нажатие ждало: INP 2,4–2,8 с,
 * у читателя с 4-ГБ ноутбуком (чат 50) — до 6,5 с на поле поиска. На главной
 * при этом тот же индекс уже лежал готовым в потоке помощника: страница
 * готовила его дважды. Разбор — docs/why/search.md («Один поток поиска на страницу»).
 *
 * Поэтому у страницы один поток: индекс готовится в нём, пока человек тянется к
 * полю (наведение, фокус, касание), каждый вопрос уходит туда, главный поток
 * только рисует ответ. Нет потоков (старый браузер, запрет политики), поток
 * упал — тот же вопрос решается на главном потоке тем же движком (одна
 * подготовка на страницу, а не по одной на каждое поле).
 *
 *   __searchThread.start(why)                 поднять заранее (идемпотентно) → 'worker' | 'main'
 *   __searchThread.search(q, opts, cb, ch)    выдача списком: cb({res, fb, other}) — fb при opts.rescue;
 *                                             при opts.other и слабой выдаче без спасения cb приходит
 *                                             ещё раз с other: {lang, list} — из индекса языка письменности
 *   __searchThread.answer(q, cb, ch)          ответ помощника (TimbricaSearch.answer); cb может
 *                                             прийти дважды — второй из индекса другого языка
 *   __searchThread.ready()                    индекс уже готов отвечать сразу
 *   __searchThread.waiting()                  индекс ещё готовится (поле пишет «Ищем…»); false,
 *                                             если он не доехал — тогда поле честно говорит «не найдено»
 *   __ensureSearchData(cb)                    сам индекс на главном потоке (/search, запасной путь)
 *
 * `ch` — канал (поле): у каждого поля в работе только его последний вопрос,
 * ответы на прежние не доставляются. Ответ проверяет и сам потребитель — по
 * строке в поле на момент ответа.
 *
 * Плоский ES5, без сборки; грузится раньше site-search.js и sidebar.js.
 */
(function () {
    'use strict';
    if (window.__searchThread) return;

    var CFG = window.__SEARCH_THREAD || {};
    // Локаль страницы — от СЕРВЕРА: переводчик браузера переписывает <html lang>.
    var loc = window.__LOCALE || document.documentElement.lang || 'en';
    function indexUrl(l) {
        return '/api/search-data?l=' + encodeURIComponent(l || loc)
            + (window.__SEARCH_VER ? '&v=' + encodeURIComponent(window.__SEARCH_VER) : '');
    }
    function feature(v) { try { if (window.__ae) window.__ae('feature_use', { f: 'search_thread', v: v }); } catch (e) {} }

    /* ── Дверь индекса на главном потоке ───────────────────────────────────
       Один запрос, одни ждущие, один массив на шапку, сайдбар, помощника и
       /search (там он уже лежит в window.__SEARCH_DATA). Переехала сюда из
       site-search.js вместе с её гейтами. */
    var tools = null, loading = false, waiters = [], broken = false;
    function ensureData(cb) {
        if (!tools && Array.isArray(window.__SEARCH_DATA) && window.__SEARCH_DATA.length) tools = window.__SEARCH_DATA;
        if (tools) { cb(tools); return; }
        waiters.push(cb);
        if (loading) return;
        loading = true;
        broken = false;
        fetch(indexUrl()).then(function (r) {
            // Окно ошибки или заглушка отдают 200 с JSON-объектом — индексом он
            // стать не должен (Sentry FH: groups.forEach на undefined).
            if (!r.ok) throw new Error('search-data ' + r.status);
            return r.json();
        }).then(function (data) {
            if (!Array.isArray(data) || !data.length) throw new Error('search-data shape');
            tools = data;
            loading = false;
            if (!window.__SEARCH_DATA) window.__SEARCH_DATA = data;
            // По порядку регистрации: последним отрабатывает самый свежий запрос.
            var w = waiters; waiters = [];
            for (var i = 0; i < w.length; i++) { try { w[i](tools); } catch (e) {} }
        }).catch(function () { loading = false; waiters = []; broken = true; });
    }
    window.__ensureSearchData = ensureData;

    /* ── Поток ─────────────────────────────────────────────────────────── */
    var mode = null, worker = null, ready = false, seq = 0;
    var live = {};                 // канал → его последний вопрос {id, kind, q, opts, cb, ch}

    function start(why) {
        if (mode) return mode;
        if (window.Worker && CFG.worker && CFG.engine) {
            try {
                worker = new window.Worker(CFG.worker);
                mode = 'worker';
                worker.onmessage = onMessage;
                worker.onerror = function () { toMain('onerror'); };
                // Индекс, уже лежащий у страницы (/search держит его в разметке), едет
                // в поток с init — второй раз его не качаем.
                var items = Array.isArray(window.__SEARCH_DATA) && window.__SEARCH_DATA.length ? window.__SEARCH_DATA : null;
                worker.postMessage({ type: 'init', engine: CFG.engine, index: indexUrl(), loc: loc, items: items });
                feature('worker:' + why);
                return mode;
            } catch (e) { /* ниже — главный поток */ }
        }
        mode = 'main';
        feature('main:' + why);
        ensureData(function () {});
        return mode;
    }

    function toMain(where) {
        if (mode === 'main') return;
        feature('fallback:' + where);
        try { if (worker) worker.terminate(); } catch (e) {}
        worker = null;
        mode = 'main';
        ready = !!prep;
        for (var ch in live) runMain(live[ch]);
    }

    function onMessage(e) {
        var m = e.data || {};
        if (m.type === 'ready') { ready = true; return; }
        if (m.type === 'error') { toMain(m.where || 'error'); return; }
        var r = null;
        for (var ch in live) if (live[ch].id === m.id) { r = live[ch]; break; }
        if (!r) return;                                  // ответ на вопрос, который уже переспросили
        if (m.type === 'results') r.cb({ res: m.res || [], fb: m.fb || null, other: m.other || null });
        else if (m.type === 'answer' && m.st) r.cb(m.st);
    }

    function ask(kind, q, opts, cb, ch) {
        if (!mode) start('typed');
        var r = { id: ++seq, kind: kind, q: q, opts: opts || {}, cb: cb, ch: ch || kind };
        live[r.ch] = r;
        if (mode === 'worker') {
            if (kind === 'search') worker.postMessage({ type: 'search', id: r.id, ch: r.ch, q: q, opts: r.opts, rescue: !!r.opts.rescue, other: !!r.opts.other });
            else worker.postMessage({ type: 'q', id: r.id, ch: r.ch, q: q });
        } else {
            runMain(r);
        }
        return r.id;
    }

    /* ── Запасной путь: тот же движок в главном потоке, одна подготовка ── */
    var prep = null, others = {};
    function engine() {
        var E = window.TimbricaSearch;
        if (!E || !tools) return null;
        if (!prep) { prep = E.prepare(tools, loc); ready = true; }
        return E;
    }
    function current(r) { return live[r.ch] === r; }
    function runMain(r) {
        ensureData(function () {
            if (!current(r)) return;                     // пока грузился индекс, человек допечатал
            var E = engine();
            if (!E) return;
            if (r.kind === 'search') {
                var res = E.search(r.q, prep, r.opts);
                var fb = (r.opts.rescue && E.rescue) ? E.rescue(r.q, prep, r.opts, res) : null;
                r.cb({ res: res, fb: fb, other: null });
                if (r.opts.other && !fb && E.weak && E.weak(res)) otherLanguage(r, null, res);
                return;
            }
            var st = E.answer(r.q, prep);
            r.cb(st);
            otherLanguage(r, st);
        });
    }
    // Запрос на чужой письменности — то же, что делает search-worker.js:
    // индекс языка этой письменности догружается один раз. `st` — ответ
    // помощника; `own` — выдача списком (тогда второй cb несёт other).
    function otherLanguage(r, st, own) {
        var E = window.TimbricaSearch;
        if (!E || !E.otherScriptLocale) return;
        if (st && st.kind !== 'empty' && st.kind !== 'fuzzy') return;
        var l2 = E.otherScriptLocale(r.q, loc);
        if (!l2) return;
        var o = others[l2];
        if (o && o.prep) {
            if (!current(r)) return;
            if (st) {
                var st2 = E.answer(r.q, o.prep);
                if (st2.kind !== 'empty' && st2.kind !== 'fuzzy') { st2.lang = l2; r.cb(st2); }
            } else {
                var res2 = E.search(r.q, o.prep, r.opts);
                if (!E.weak(res2)) r.cb({ res: own, fb: null, other: { lang: l2, list: res2 } });
            }
            return;
        }
        if (o) return;
        o = others[l2] = { prep: null };
        fetch(indexUrl(l2))
            .then(function (x) { if (!x.ok) throw new Error('search-data ' + x.status); return x.json(); })
            .then(function (items) {
                if (!Array.isArray(items) || !items.length) return;
                o.prep = E.prepare(items, l2);
                if (current(r)) otherLanguage(r, st, own);
            }, function () {});
    }

    window.__searchThread = {
        start: start,
        search: function (q, opts, cb, ch) { return ask('search', q, opts, cb, ch); },
        answer: function (q, cb, ch) { return ask('answer', q, null, cb, ch); },
        ready: function () { return ready; },
        waiting: function () { return !!mode && !ready && !(mode === 'main' && broken); },
        mode: function () { return mode; }
    };
})();
